import React, { useEffect, useRef, useState } from "react";
import { Row, Col, Container, Stack, Button, Spinner, Image } from "react-bootstrap";
import { useNavigate, useLocation, Link } from "react-router-dom";
import {
  fetchBeds,
  fetchCases,
  fetchCaseStatuses,
  fetchCMUsers,
  fetchEthnicities,
  fetchGenders,
  fetchJobs,
  fetchPrograms,
  fetchRaces,
  fetchRelationships,
  fetchRooms,
  patchGuardian,
  postNote,
  fetchFields,
  postFieldValue,
  postGuardian,
  postCase,
  patchCase,
  postJobAssignment,
  postBedOccupant,
  postProgramAssignment,
} from "../../services/httpClient";
import PageTitle from "../ui/PageTitle";
import SelectObject from "../ui/SelectObject";
import Input from "../ui/Input";
import SaveChangesModal from "../ui/SaveChangesModal";
import { unsavedChanges, currentUrl } from "../../App";
import useApp from "../../utils/useApp";
import InputPhone from "../ui/InputPhone";
import FormCheck from "../ui/FormCheckbox";
import DatePicker from "../ui/DatePicker";
import SelectObjectTypeahead from "../ui/SelectObjectTypeahead";
import IntakeNote from "../ui/IntakeNote";
import StandardNote from "../ui/StandardNote";
import CustomField from "../ui/CustomField";
import { UserFields } from "../../types/userFields";
import { validateUserFields } from "../../helpers/validation";

const CreateCase = () => {
  const { showMenu, auth }: any = useApp();
  const location = useLocation();
  currentUrl.value = location.pathname;
  const [showMoreData, setShowMoreData] = useState<boolean>(false);
  const [custom, setCustom] = useState<any[]>([]);
  const [suggestionOne, setSuggestionOne] = useState<string>("");
  const [suggestionTwo, setSuggestionTwo] = useState<string>("");
  const [name, setName] = useState<string>("");
  const [primaryCM, setPrimaryCM] = useState<string>("");
  const [secondaryCM, setSecondaryCM] = useState<string>("");
  const [status, setStatus] = useState<string>("active");
  const [cmUsers, setCmUsers] = useState<any[]>([]);
  const [cases, setCases] = useState<any[]>([]);
  const [statuses, setStatuses] = useState<any[]>([]);
  const [relationships, setRelationships] = useState<any[]>([]);
  const [genders, setGenders] = useState<any[]>([]);
  const [races, setRaces] = useState<any[]>([]);
  const [ethnicities, setEthnicities] = useState<any[]>([]);
  const [programs, setPrograms] = useState<any[]>([]);
  const [levels, setLevels] = useState<any[]>([]);
  const [jobs, setJobs] = useState<any[]>([]);
  const [rooms, setRooms] = useState<any[]>([]);
  const [beds, setBeds] = useState<any[]>([]);
  const [filteredBeds, setFilteredBeds] = useState<any[]>([]);
  const [customFields, setCustomFields] = useState<any[]>([]);
  const [noteDate, setNoteDate] = useState<string>(new Date().toISOString().split("T")[0]);
  const [note, setNote] = useState<string>("");
  const [showModal, setShowModal] = useState<boolean>(false);
  const [saveIsEnabled, setSaveIsEnabled] = useState<boolean>(false);
  const [errMsg, setErrMsg] = useState("");
  const [existingCase, setExistingCase] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isImageLoading, setIsImageLoading] = useState<boolean>(false);
  const [profileImage, setProfileImage] = useState<string>("");
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const fileCaptureRef = useRef<HTMLInputElement | null>(null);
  const [showCamera, setShowCamera] = useState<boolean>(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [userFields, setUserFields] = useState<UserFields>({
    firstName: "",
    lastName: "",
    preferredName: "",
    email: "",
    phone: null,
    intakeDate: undefined,
    exitDate: undefined,
    birthDate: undefined,
    relationship: "",
    gender: "",
    ethnicity: "",
    race: "",
    school: "",
    grade: "",
    program: null,
    level: null,
    job: null,
    room: "",
    bed: null,
    isPrimary: false,
    isPersonServed: true,
    isResidential: false,
    appAccess: false,
    appAccessLast: undefined,
    isBanned: false,
  });

  let navigate = useNavigate();

  const backCase = () => {
    if (saveIsEnabled) {
      setShowModal(true);
    } else {
      navigate(`/cases`);
    }
  };

  const editCase = (caseId: string, toastText?: string, toastBody?: string, errorSummary?: string) => {
    navigate(`/cases/case/${caseId}`, {
      state: {
        toastText,
        toastBody,
        errorSummary,
      },
    });
  };

  useEffect(() => {
    setIsLoading(true);
    getCaseManagers();
    getCaseStatuses();
    getCases();
    getRelationships();
    getGenders();
    getRaces();
    getEthnicities();
    getPrograms();
    getJobs();
    if (auth?.profile.includeHMIS === 1) {
      getBeds();
    }
    getCustomFields();
  }, []);

  //switch to get case managers
  const getCaseManagers = () => {
    fetchCMUsers()
      .then((res: any) => {
        const users = res[0].data.map((i: any) => {
          return {
            id: i.id,
            key: i.id,
            name: i.firstname.length === 0 ? i.email : `${i.firstname} ${i.lastname ?? ""}`,
          };
        });
        setCmUsers(users);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const getCaseStatuses = async () => {
    await fetchCaseStatuses().then((res: any) => {
      setStatuses(res[0].data);
      setStatus(location.pathname.split("/")[4].toLocaleLowerCase());
    });
  };

  const getCases = () => {
    fetchCases().then((res: any) => {
      setCases(res[0].data);
    });
  };

  const getRelationships = () => {
    fetchRelationships().then((res: any) => {
      setRelationships(res[0].data);
    });
  };

  const getGenders = () => {
    fetchGenders().then((res: any) => {
      setGenders(res[0].data);
    });
  };

  const getRaces = () => {
    fetchRaces().then((res: any) => {
      setRaces(res[0].data);
    });
  };

  const getEthnicities = () => {
    fetchEthnicities().then((res: any) => {
      setEthnicities(res[0].data);
    });
  };

  const getPrograms = () => {
    fetchPrograms().then((res: any) => {
      var programs = res[0].data;
      programs = [{ id: 0, key: 0, name: "Select from List", programLevels: [] }, ...programs];
      setPrograms(programs);
    });
  };

  const getJobs = () => {
    fetchJobs().then((res: any) => {
      const jobs = res[0].data;
      var availJobs = jobs.filter((j: any) => j.positions_filled < j.positions_total);
      availJobs = [{ id: 0, key: 0, name: "Select from List" }, ...availJobs];
      setJobs(availJobs);
    });
  };

  const getBeds = () => {
    fetchBeds()
      .then((res: any) => {
        var bedList = res[0].data;
        //filter to vacant beds
        bedList = bedList.filter((b: any) => b.occupant_id === null);

        bedList.sort((a: any, b: any) => {
          // group by room name
          if (a.room_name !== b.room_name) {
            return a.room_name.localeCompare(b.room_name, undefined, { numeric: true, sensitivity: "base" });
          }
          // sort beds in each room alphanumerically
          return a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: "base" });
        });

        setBeds(bedList);
        setFilteredBeds(bedList);
        getRooms();
      })
      .catch((err: any) => {
        console.log(err);
      })
      .finally(() => {
        setErrMsg("");
      });
  };

  const getRooms = () => {
    fetchRooms()
      .then((res: any) => {
        const roomList = res[0].data;
        roomList.sort((a: any, b: any) => {
          return a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: "base" });
        });
        setRooms(roomList);
      })
      .catch((err: any) => {
        console.log(err);
      });
  };

  const getCustomFields = async () => {
    await fetchFields().then((res: any) => {
      setCustomFields(res[0].data);
    });
  };

  function validateUser(
    first: string,
    last: string,
    emailVal: any,
    phoneVal: any,
    level: any,
    levelRequired: boolean,
    appAccess: boolean = false,
  ) {
    let errors: string[] = [];
    errors = validateUserFields(first, last, emailVal, phoneVal, level, levelRequired, appAccess);

    return {
      isValid: errors.length === 0,
      errors: errors,
    };
  }

  const updateField = (field: keyof typeof userFields, value: UserFields[keyof UserFields], levelRequired = levels.length > 0) => {
    setUserFields((prev) => {
      const updated = { ...prev, [field]: value };
      const validation = validateUser(
        updated.firstName,
        updated.lastName,
        updated.email,
        updated.phone,
        updated.level,
        levelRequired,
        updated.appAccess,
      );
      setSaveIsEnabled(validation.isValid && name ? true : false);
      return updated;
    });
  };

  useEffect(() => {
    if (saveIsEnabled) {
      unsavedChanges.value = true;
    } else {
      unsavedChanges.value = false;
    }
  }, [saveIsEnabled]);

  const updateProgram = (e: any) => {
    updateField("level", null);
    let updatedLevels = [];
    setLevels([]);
    if (e.programLevels && e.programLevels.length > 0) {
      updatedLevels = e.programLevels;
    }
    setLevels(updatedLevels);
    updateField("program", e, updatedLevels.length > 0);
  };

  const updateLevel = (e: any) => {
    if (e.key !== userFields.level?.key) {
      updateField("level", e);
    }
  };

  const updateJob = (e: any) => {
    if (e.key !== userFields.job?.key) {
      updateField("job", e);
    }
  };

  const updateRoom = (e: any) => {
    updateField("room", e.key);
    updateField("bed", null);
    const filtered = beds.filter((b: any) => b.room_key === e.key);
    setFilteredBeds(filtered);
  };

  const updateBed = (e: any) => {
    updateField("bed", e);
  };

  function setSuggestions(first: string, last: string) {
    const suggestionOne = last + " Family";
    const existingCase = cases.find((i: any) => i.name === suggestionOne);

    if (existingCase) {
      setSuggestionOne(first + " " + suggestionOne);
    } else {
      setSuggestionOne(suggestionOne);
    }

    const suggestionTwo = last + ", " + first;
    setSuggestionTwo(suggestionTwo);
  }

  const updateName = (e: any) => {
    setName(e.target.value);
    const validation = validateUser(
      userFields.firstName,
      userFields.lastName,
      userFields.email,
      userFields.phone,
      userFields.level,
      levels.length > 0,
      userFields.appAccess,
    );
    if (e.target.value && validation.isValid) {
      setSaveIsEnabled(true);
    } else {
      setSaveIsEnabled(false);
    }
  };

  const updatePrimaryCM = (e: any) => {
    setPrimaryCM(e.target.value);
  };

  const updateSecondaryCM = (e: any) => {
    setSecondaryCM(e.target.value);
  };

  // a new person is only a person served by default on an active case
  useEffect(() => {
    setUserFields((prev) => ({ ...prev, isPersonServed: status === "active" }));
  }, [status]);

  const updateCaseStatus = (e: any) => {
    setStatus(e.target.value);
    // Setting URL to new value to avoid confusion
    navigate(`/cases/case/create/${e.target.value.charAt(0).toUpperCase() + e.target.value.slice(1)}`);
  };

  const onClickSuggestionOne = () => {
    setName(suggestionOne);

    const validation = validateUser(
      userFields.firstName,
      userFields.lastName,
      userFields.email,
      userFields.phone,
      userFields.level,
      levels.length > 0,
      userFields.appAccess,
    );
    if (validation.isValid && suggestionOne) {
      setSaveIsEnabled(true);
    } else {
      setSaveIsEnabled(false);
    }
  };

  const onClickSuggestionTwo = () => {
    setName(suggestionTwo);

    const validation = validateUser(
      userFields.firstName,
      userFields.lastName,
      userFields.email,
      userFields.phone,
      userFields.level,
      levels.length > 0,
      userFields.appAccess,
    );
    if (validation.isValid && suggestionTwo) {
      setSaveIsEnabled(true);
    } else {
      setSaveIsEnabled(false);
    }
  };

  const saveCase = async () => {
    const validation = validateUser(
      userFields.firstName,
      userFields.lastName,
      userFields.email,
      userFields.phone,
      userFields.level,
      levels.length > 0,
      userFields.appAccess,
    );

    if (!(validation.isValid && name)) {
      validation.errors.map((err: string) => {
        setErrMsg((prev) => (prev ? prev + "  |  " + err : err));
        return true;
      });
      return;
    }

    setIsLoading(true);
    try {
      const newCase = {
        name: name,
        primaryCM: primaryCM,
        secondaryCM: secondaryCM,
        casestatus: status,
      };

      let caseId: string;
      try {
        const res: any = await postCase(newCase);
        caseId = res[0].data.case_id;
      } catch (err: any) {
        console.log(err);
        setErrMsg("Case Creation Failed: " + err?.response?.data?.msg);
        const matchingCase = cases.find((c: any) => c.name.toLowerCase() === name.toLowerCase());
        setExistingCase(matchingCase ? matchingCase.id : null);
        unsavedChanges.value = true;
        return;
      }

      // The case now exists server-side, so from here on we always land on Edit Case -
      // everything below is a recoverable follow-up step, not a reason to stay on Create.
      const errors: string[] = [];

      var newUser: any = {
        is_case_subject: userFields.isPersonServed ? 1 : 0,
        is_residential: userFields.isResidential ? 1 : 0,
        appaccess: userFields.appAccess,
        email: userFields.email,
        firstname: userFields.firstName,
        lastname: userFields.lastName,
        preferredname: userFields.preferredName,
        phonenumber: userFields.phone === "1" ? null : userFields.phone,
        birthdate: userFields.birthDate,
        relationship: userFields.relationship,
        gender: userFields.gender,
        ethnicity: userFields.ethnicity,
        race: userFields.race,
      };

      let userId: string | undefined;
      try {
        const res: any = await postGuardian(newUser, caseId);
        userId = res[0].data.user_id;
      } catch (err: any) {
        console.log(err);
        errors.push("Primary Adult Creation Failed" + (err?.response?.data?.msg ? `: ${err.response.data.msg} ` : " "));
      }

      if (userId) {
        const tasks: Promise<any>[] = [];
        const updatedCase = { family: [userId] };
        tasks.push(
          patchCase(updatedCase, caseId).catch((err: any) => {
            console.log(err);
            errors.push("Primary Adult Created, but Not Linked to the Case ");
          }),
        );

        if (profileImage) {
          const image = { profile_data: profileImage.split(",")[1] };
          tasks.push(
            patchGuardian(image, caseId, userId).catch((err: any) => {
              console.log(err);
              errors.push("Profile Photo Failed ");
            }),
          );
        }

        //update / create custom field values
        custom.forEach((cust: any) => {
          const fieldVal = {
            field_id: cust?.field_id,
            person_id: userId,
            person_type: "guardian",
            value: cust?.value,
          };

          const fieldLabel = customFields.find((f: any) => (f?.field_id ?? f?.id) === cust?.field_id)?.label;

          tasks.push(
            postFieldValue(fieldVal).catch((err: any) => {
              console.log(err);
              errors.push(`Custom Field${fieldLabel ? ` "${fieldLabel}"` : ""} Failed `);
            }),
          );
        });

        //save intake note
        if (status === "active") {
          const newNote = {
            notetype: "intake",
            subject: {
              id: userId,
              type: "guardian",
            },
            fields: {
              description: note,
              date: new Date().toISOString().split("T")[0],
            },
          };

          tasks.push(
            postNote(newNote, caseId)
              .then((res: any) => {
                const noteId = res[0].data.note_id;

                //update user intake data
                const intakeData = {
                  intake_at: noteDate,
                  intake_note_id: noteId,
                };

                return patchGuardian(intakeData, caseId, userId).catch((err: any) => {
                  console.log(err);
                  errors.push("Primary Adult Failed to Update Intake Date ");
                });
              })
              .catch((err: any) => {
                console.log(err);
                errors.push("Intake Note Failed" + (err?.response?.data?.msg ? `: ${err.response.data.msg} ` : " "));
              }),
          );
        } else if (note) {
          const newNote = {
            notetype: "standard",
            subject: {
              id: userId,
              type: "guardian",
            },
            fields: {
              text: note,
              date: noteDate,
            },
          };

          tasks.push(
            postNote(newNote, caseId).catch((err: any) => {
              console.log(err);
              errors.push("Standard Note Failed" + (err?.response?.data?.msg ? `: ${err.response.data.msg} ` : " "));
            }),
          );
        }

        if (userFields.job) {
          const newJob = {
            job_id: userFields.job?.id,
            user_id: userId,
          };

          tasks.push(
            postJobAssignment(newJob, caseId).catch((err: any) => {
              console.log(err);
              errors.push("Job Assignment Failed" + (err?.response?.data?.msg ? `: ${err.response.data.msg} ` : " "));
            }),
          );
        }

        if (userFields.program) {
          const newProgram = {
            program_id: userFields.program?.id,
            level_id: userFields.level?.id,
            user_id: userId,
          };

          tasks.push(
            postProgramAssignment(newProgram, caseId).catch((err: any) => {
              console.log(err);
              errors.push("Program Assignment Failed" + (err?.response?.data?.msg ? `: ${err.response.data.msg} ` : " "));
            }),
          );
        }

        if (auth?.profile.includeHMIS === 1 && userFields.bed) {
          const newBed = {
            bed_id: userFields.bed?.id,
            user_id: userId,
          };

          tasks.push(
            postBedOccupant(newBed, caseId).catch((err: any) => {
              console.log(err);
              errors.push("Bed Assignment Failed" + (err?.response?.data?.msg ? `: ${err.response.data.msg} ` : " "));
            }),
          );
        }

        await Promise.allSettled(tasks);
      }

      unsavedChanges.value = false;
      setSaveIsEnabled(false);

      if (errors.length > 0) {
        editCase(caseId, undefined, undefined, errors.join("  |  "));
      } else {
        editCase(caseId, "Case Created", name);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const onClickSelect = (event: any) => {
    fileInputRef?.current?.click();
  };

  const handleFileChange = (event: any) => {
    setErrMsg("");
    setIsImageLoading(true);

    const file = event.target.files[0];
    if (file) {
      if (file.type.startsWith("image/")) {
        var reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = function () {
          if (reader.result) {
            uploadFileData(reader.result.toString().split(",")[1]);
          }
        };
        reader.onerror = function (error) {
          setIsImageLoading(false);
        };
      } else {
        setErrMsg("Please select a valid image file");
        setIsImageLoading(false);
      }
    } else {
      setIsImageLoading(false);
    }
  };

  async function uploadFileData(data: any) {
    setProfileImage(data);
    const base64String = "data:image/jpeg;base64, " + data;
    setProfileImage(base64String);
    setIsImageLoading(false);
  }

  const onClickStartCamera = async () => {
    try {
      setShowCamera(true);
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (err) {
      console.error("Camera error:", err);
      alert("Unable to access camera.");
    }
  };

  const onClickTakePicture = () => {
    if (!videoRef.current) return;
    setShowCamera(false);
    const canvas = document.createElement("canvas");
    canvas.width = videoRef.current.videoWidth;
    canvas.height = videoRef.current.videoHeight;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height);

    canvas.toBlob((blob) => {
      if (blob) {
        const file = new File([blob], "photo.png", { type: "image/png" });
        var reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = function () {
          if (reader.result) {
            uploadFileData(reader.result.toString().split(",")[1]);
          }
        };
      }
    }, "image/png");
  };

  const onClickDeleteProfilePic = () => {
    setProfileImage("");
  };

  const renderDetails = () => {
    return (
      <>
        <h4 className="app-header-text mb-3">Details</h4>
        {userFields.lastName && (
          <Row>
            <Col xs={12} className="d-flex gap-3">
              <h6>Select a Suggested Case Name:</h6>
              <h6 className="app-tertiary-color-inverse cursor-pointer" onClick={() => onClickSuggestionOne()}>
                {suggestionOne}
              </h6>
              <h6 className="app-tertiary-color-inverse cursor-pointer" onClick={() => onClickSuggestionTwo()}>
                {suggestionTwo}
              </h6>
            </Col>
          </Row>
        )}
        <Row>
          <Col xs={12} lg={12} xl={12} className="mb-3">
            <Input
              label="Name"
              type="text"
              value={name}
              onUpdate={(e: any) => updateName(e)}
              required={true}
              showCharCount
              maxLength={75}
            />
          </Col>
        </Row>
        <Row>
          <Col xs={12} lg={12} xl={6} className="mb-3">
            <SelectObject label="Primary Case Manager" value={primaryCM} options={cmUsers} onUpdate={(e: any) => updatePrimaryCM(e)} />
          </Col>
          <Col xs={12} lg={12} xl={6} className="mb-3">
            <SelectObject
              label="Secondary Case Manager"
              value={secondaryCM}
              options={cmUsers}
              onUpdate={(e: any) => updateSecondaryCM(e)}
            />
          </Col>
        </Row>
        <Row>
          <Col xs={12} lg={12} xl={6} className="mb-3">
            <SelectObject
              label="Status"
              value={status}
              options={statuses}
              onUpdate={(e: any) => updateCaseStatus(e)}
              skipInitialOption={true}
            />
          </Col>
        </Row>
      </>
    );
  };

  return (
    <>
      {isLoading ? (
        <Container className={showMenu ? "lg div-main-menu" : "lg div-main"}>
          <Button size="sm" className="spinner-button spinner-button-main">
            <Spinner animation="border" style={{ color: "#F6893D" }} />
          </Button>
        </Container>
      ) : (
        <Container className={showMenu ? "lg div-main-menu" : "lg div-main"}>
          <p className={errMsg ? "errmsg" : "offscreen"} aria-live="assertive">
            {errMsg}
            {existingCase && (
              <Link className={"ms-2"} style={{ color: "#5a7082" }} to={`/cases/case/${existingCase}`}>
                View Case
              </Link>
            )}
          </p>
          <PageTitle
            title={"Create Case"}
            buttonTitle="Save"
            onButtonClick={saveCase}
            buttonDisabled={!saveIsEnabled}
            buttonColor="secondary"
            backButtonText="Back to Cases"
            onBackClick={backCase}
          />
          <Row>
            <Col xs={12} lg={6} className="mb-3">
              <h4 className="app-header-text mb-3">Primary Adult</h4>
              <Row>
                <Col xs={12} lg={12} xl={6} className="mb-3">
                  <Input
                    label="First Name"
                    type="text"
                    value={userFields.firstName}
                    onUpdate={(e: any) => {
                      updateField("firstName", e.target.value);
                      setSuggestions(e.target.value, userFields.lastName);
                    }}
                    required={true}
                    hasFocus={true}
                  />
                </Col>
                <Col xs={12} lg={12} xl={6} className="mb-3">
                  <Input
                    label="Last Name"
                    type="text"
                    value={userFields.lastName}
                    onUpdate={(e: any) => {
                      updateField("lastName", e.target.value);
                      setSuggestions(userFields.firstName, e.target.value);
                    }}
                    required={true}
                  />
                </Col>
              </Row>
              <Row>
                <Col xs={12} lg={12} xl={6} className="mb-3">
                  <Input
                    label="Email"
                    type="text"
                    value={userFields.email}
                    onUpdate={(e: any) => updateField("email", e.target.value)}
                    required={userFields.appAccess}
                  />
                </Col>
                <Col xs={12} lg={12} xl={6} className="mb-3">
                  <InputPhone
                    label="Phone"
                    value={userFields.phone}
                    onUpdate={(e: any) => updateField("phone", e)}
                    required={userFields.appAccess}
                  />
                </Col>
              </Row>
              <Row>
                <Col xs={8} className="mb-3">
                  <Stack gap={2}>
                    <FormCheck
                      label="Person Served"
                      value={userFields.isPersonServed}
                      onUpdate={(e: any) => updateField("isPersonServed", !userFields.isPersonServed)}
                    />
                    {auth?.profile.includeHMIS === 1 && (
                      <FormCheck
                        label="Residential"
                        value={userFields.isResidential}
                        onUpdate={(e: any) => updateField("isResidential", !userFields.isResidential)}
                      />
                    )}
                    <Stack>
                      <FormCheck
                        label="Mobile App Access"
                        css="mb-0"
                        value={userFields.appAccess}
                        onUpdate={(e: any) => updateField("appAccess", !userFields.appAccess)}
                      />
                      <small>{"(Requires email or phone)"}</small>
                    </Stack>
                  </Stack>
                </Col>
                <Col xs={4} className="text-end mb-3">
                  <Button className="app-primary-color" onClick={() => setShowMoreData(!showMoreData)}>
                    {showMoreData ? "Hide Additional Data" : "Show Additional Data"}
                  </Button>
                </Col>
              </Row>
              {showMoreData && (
                <>
                  <Row>
                    <Col xs={12} lg={12} xl={6} className="mb-3">
                      <Input
                        label="Preferred Name"
                        type="text"
                        value={userFields.preferredName}
                        onUpdate={(e: any) => updateField("preferredName", e.target.value)}
                      />
                    </Col>
                    <Col xs={12} lg={12} xl={6} className="mb-3">
                      <DatePicker
                        label="Birth Date"
                        currentValue={userFields.birthDate}
                        setCurrentValue={(e: any) => updateField("birthDate", e)}
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col xs={12} lg={12} xl={6} className="mb-3">
                      <SelectObject
                        label="Relationship"
                        value={userFields.relationship}
                        options={relationships}
                        onUpdate={(e: any) => updateField("relationship", e.target.value)}
                      />
                    </Col>
                    <Col xs={12} lg={12} xl={6} className="mb-3">
                      <SelectObject
                        label="Gender"
                        value={userFields.gender}
                        options={genders}
                        onUpdate={(e: any) => updateField("gender", e.target.value)}
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col xs={12} lg={12} xl={6} className="mb-3">
                      <SelectObject
                        label="Ethnicity"
                        value={userFields.ethnicity}
                        options={ethnicities}
                        onUpdate={(e: any) => updateField("ethnicity", e.target.value)}
                      />
                    </Col>
                    <Col xs={12} lg={12} xl={6} className="mb-3">
                      <SelectObject
                        label="Race"
                        value={userFields.race}
                        options={races}
                        onUpdate={(e: any) => updateField("race", e.target.value)}
                      />
                    </Col>
                  </Row>
                  {(programs.length > 0 || jobs.length > 0) && (
                    <Row>
                      <Col xs={12} lg={12} xl={6} className="mb-3">
                        {programs.length > 0 && (
                          <SelectObjectTypeahead
                            label="Program"
                            value={userFields.program?.key}
                            options={programs}
                            onUpdate={(e: any) => updateProgram(e)}
                          />
                        )}
                      </Col>
                      {levels.length > 0 && (
                        <Col xs={12} lg={12} xl={6} className="mb-3">
                          <SelectObjectTypeahead
                            label="Program Level"
                            value={userFields.level?.key}
                            options={levels}
                            onUpdate={(e: any) => updateLevel(e)}
                          />
                        </Col>
                      )}
                      {jobs.length > 0 && (
                        <Col xs={12} lg={12} xl={6} className="mb-3" style={levels.length > 0 ? { marginTop: "1rem" } : {}}>
                          <SelectObjectTypeahead
                            label="Job"
                            value={userFields.job?.key}
                            options={jobs}
                            onUpdate={(e: any) => updateJob(e)}
                          />
                        </Col>
                      )}
                    </Row>
                  )}
                  {rooms.length > 0 && beds.length > 0 && (
                    <Row>
                      <Col xs={12} lg={12} xl={6} className="mb-3">
                        <SelectObjectTypeahead label="Room" value={userFields.room} options={rooms} onUpdate={(e: any) => updateRoom(e)} />
                      </Col>
                      <Col xs={12} lg={12} xl={6} className="mb-3">
                        <SelectObjectTypeahead
                          label="Bed"
                          value={userFields.bed?.key}
                          options={filteredBeds}
                          onUpdate={(e: any) => updateBed(e)}
                          disabled={userFields.room ? false : true}
                          initialOption="Vacant"
                        />
                      </Col>
                    </Row>
                  )}
                  {customFields?.length > 0 && (
                    <Row className="h-auto">
                      {customFields?.map((c: any) => (
                        <CustomField
                          customField={c}
                          setCustom={setCustom}
                          enableSave={() =>
                            validateUser(
                              userFields.firstName,
                              userFields.lastName,
                              userFields.email,
                              userFields.phone,
                              userFields.level,
                              levels.length > 0,
                              userFields.appAccess,
                            )
                          }
                          setSaveIsEnabled={setSaveIsEnabled}
                          columns={6}
                          editMode={true}
                        />
                      ))}
                    </Row>
                  )}
                  <Row className="mt-3">
                    <Col xs={8}>
                      {isImageLoading ? (
                        <Button size="sm" className="spinner-button spinner-button-main">
                          <Spinner animation="border" style={{ color: "#F6893D" }} />
                        </Button>
                      ) : (
                        <Stack gap={3}>
                          {showCamera ? (
                            <>
                              <video ref={videoRef} autoPlay playsInline className="w-64 h-48 border" />
                              <Stack direction="horizontal" gap={3} className="justify-content-center">
                                <Button className="app-primary-color-inverse button w-100 ms-3" onClick={onClickTakePicture}>
                                  Capture
                                </Button>
                                <Button className="app-primary-color button w-100 me-3" onClick={() => setShowCamera(false)}>
                                  Cancel
                                </Button>
                              </Stack>
                            </>
                          ) : profileImage ? (
                            <Image src={profileImage} rounded className="image-profile" />
                          ) : (
                            <Image src="/blank-profile-picture.jpg" className="image-profile" />
                          )}
                          {!showCamera && (
                            <Stack direction="horizontal" gap={3} className="justify-content-center">
                              <Button className="app-primary-color-inverse button w-100 ms-3" onClick={(e: any) => onClickSelect(e)}>
                                Select Photo
                              </Button>
                              <Button className="app-primary-color-inverse button w-100 me-3" onClick={onClickStartCamera}>
                                Take Photo
                              </Button>
                            </Stack>
                          )}
                          {profileImage && !showCamera && (
                            <Button className="app-danger-color button mx-3" onClick={() => onClickDeleteProfilePic()}>
                              Remove Image
                            </Button>
                          )}
                        </Stack>
                      )}
                      <input
                        type="file"
                        ref={fileInputRef}
                        style={{ display: "none" }}
                        accept="image/*"
                        onChange={handleFileChange} // Handle the file change
                      />
                      <input
                        type="file"
                        ref={fileCaptureRef}
                        style={{ display: "none" }}
                        accept="image/*"
                        capture="user"
                        onChange={handleFileChange} // Handle the file change
                      />
                    </Col>
                  </Row>
                </>
              )}
              {!showMoreData && renderDetails()}
            </Col>
            <Col xs={12} lg={6} className="mb-3">
              <h4 className="app-header-text">Note</h4>
              <Row className="mb-3">
                {status === "active" ? (
                  <IntakeNote intakeNote={note} setIntakeNote={setNote} intakeDate={noteDate} setIntakeDate={setNoteDate} />
                ) : (
                  <StandardNote note={note} setNote={setNote} noteDate={noteDate} setNoteDate={setNoteDate} />
                )}
              </Row>
              {showMoreData && renderDetails()}
            </Col>
          </Row>
          <SaveChangesModal showModal={showModal} setShowModal={setShowModal} path="/cases" />
        </Container>
      )}
    </>
  );
};

export default CreateCase;
